import { ArrowUpRight, Code2, Feather, Sparkles } from "lucide-react";
import logo from "../assets/brand/maritaca-logo.svg";
import logoLight from "../assets/brand/maritaca-logo-dark.svg";
import "./login-design.css";

export type LoginDesignVariant = "atual" | "essencial" | "editorial" | "tecnica" | "organica" | "editorial-direta" | "editorial-indice" | "editorial-discreta" | "editorial-e3a" | "editorial-e3b" | "editorial-e3c" | "editorial-marfim" | "editorial-salvia" | "editorial-areia" | "editorial-verde-escuro" | "radial-suave" | "radial-original";

export default function LoginDesignPanel({ variant }: { variant: LoginDesignVariant }) {
  const radial = variant === "radial-suave" || variant === "radial-original";
  const colorStudy = radial || ["editorial-discreta", "editorial-marfim", "editorial-salvia", "editorial-areia", "editorial-verde-escuro"].includes(variant);
  const light = variant.startsWith("editorial") && variant !== "editorial-verde-escuro";
  return (
    <section data-testid="platform-value-panel" className={"login-direction login-direction-" + variant + (radial ? " login-direction-editorial-verde-escuro" : "")}>
      {variant === "organica" && <img className="direction-art" src="/maritaca-site-hero.png" alt="" />}
      {radial && <>
        <img className="login-radial-art" src="/maritaca-site-hero.png" alt="" aria-hidden="true" />
        <div className="login-radial-vignette" aria-hidden="true" />
      </>}
      <header className="direction-brand">
        <img src={light ? logo : logoLight} alt="Maritaca AI" />
        {!colorStudy && <span>PLATAFORMA</span>}
      </header>

      {variant === "essencial" && (
        <>
          <div className="direction-body essential-direction">
            <span className="direction-eyebrow"><span className="direction-dot" /> INTELIGÊNCIA BRASILEIRA</span>
            <h1>Sua próxima ideia.<br /><span>Com Sabiá.</span></h1>
            <p>Um espaço para experimentar, integrar e acompanhar suas aplicações de inteligência artificial.</p>
            <div className="direction-rule" />
            <div className="essential-directions">
              <span>Experimente no Playground <ArrowUpRight /></span>
              <span>Integre pela API <ArrowUpRight /></span>
              <span>Acompanhe seu consumo <ArrowUpRight /></span>
            </div>
          </div>
          <footer className="direction-footer"><span>Feito no Brasil.</span><Feather size={21} /></footer>
        </>
      )}

      {variant === "editorial-direta" && (
        <>
          <div className="direction-body editorial-direction editorial-direct">
            <span className="direction-eyebrow">PLATAFORMA MARITACA</span>
            <h1>Modelos.<br />API.<br /><span>Consumo.</span></h1>
            <p>Teste os modelos Sabiá, gerencie suas chaves de API e acompanhe o uso da sua conta.</p>
          </div>
          <footer className="editorial-note">Modelos Sabiá · Maritaca AI</footer>
        </>
      )}

      {variant === "editorial-indice" && (
        <>
          <div className="direction-body editorial-index">
            <span className="direction-eyebrow">PLATAFORMA MARITACA</span>
            <h1>Seu espaço<br />de trabalho.</h1>
            <p>Acesso aos modelos e à gestão da sua conta.</p>
            <dl className="editorial-index-list">
              <div><dt><span>01</span>Playground</dt><dd>Teste prompts e compare respostas.</dd></div>
              <div><dt><span>02</span>Chaves de API</dt><dd>Crie e gerencie suas chaves.</dd></div>
              <div><dt><span>03</span>Consumo</dt><dd>Consulte o uso e os custos.</dd></div>
            </dl>
          </div>
          <footer className="editorial-note">Maritaca AI</footer>
        </>
      )}

      {["editorial-e3a", "editorial-e3b", "editorial-e3c"].includes(variant) && (
        <>
          <div className="direction-body editorial-quiet editorial-refinement">
            <span className="direction-eyebrow">PLATAFORMA</span>
            <h1>Maritaca<span>para desenvolvedores.</span></h1>
            <div className="editorial-short-rule" />
            <p>Acesse os modelos Sabiá e gerencie sua integração com a API.</p>
          </div>
          <footer className="editorial-note">Playground · Chaves de API · Consumo</footer>
        </>
      )}

      {colorStudy && (
        <>
          <div className="direction-body editorial-quiet">
            <h1>Maritaca<span>para desenvolvedores.</span></h1>
            <div className="editorial-short-rule" />
            <p>Acesse os modelos Sabiá e gerencie sua integração com a API.</p>
          </div>
          <footer className="editorial-note">Playground · Chaves de API · Consumo</footer>
        </>
      )}

      {variant === "editorial" && (
        <>
          <div className="direction-body editorial-direction">
            <span className="direction-eyebrow">DA IDEIA À APLICAÇÃO</span>
            <h1>Imagine.<br />Construa.<br /><span>Publique.</span></h1>
            <p>Inteligência artificial brasileira para transformar o que você imagina em algo que as pessoas usam.</p>
          </div>
          <footer className="editorial-footer">
            <div><span>01</span><strong>Explore os modelos</strong></div>
            <div><span>02</span><strong>Crie com a API</strong></div>
            <div><span>03</span><strong>Acompanhe os resultados</strong></div>
          </footer>
          <Sparkles className="editorial-spark" aria-hidden="true" />
        </>
      )}

      {variant === "tecnica" && (
        <>
          <div className="direction-body technical-direction">
            <span className="direction-eyebrow"><Code2 size={16} /> PARA QUEM CONSTRÓI</span>
            <h1>Menos distância<br />entre ideia e código.</h1>
            <p>Teste seus prompts. Escolha seu modelo.<br />Leve o Sabiá para a sua aplicação.</p>
            <div className="direction-code">
              <div className="direction-code-header"><span>Uma conversa começa aqui</span><span>JSON</span></div>
              <pre aria-label="Exemplo de requisição à API"><code>{"{\n  \"model\": \"sabia-4\",\n  \"messages\": [\n    {\n      \"role\": \"user\",\n      \"content\": \"Vamos construir?\"\n    }\n  ]\n}"}</code></pre>
              <div className="direction-code-footer"><span className="direction-dot" /> API compatível com OpenAI</div>
            </div>
          </div>
          <footer className="direction-footer"><span>Playground · API · Consumo</span><Code2 size={20} /></footer>
        </>
      )}

      {variant === "organica" && (
        <>
          <div className="direction-body organic-direction">
            <span className="organic-symbol"><Feather size={30} strokeWidth={1.3} /></span>
            <span className="direction-eyebrow">ORIGEM BRASILEIRA. NOVAS POSSIBILIDADES.</span>
            <h1>A inteligência<br />ganha asas.</h1>
            <p>Do primeiro experimento à sua próxima aplicação. Crie com os modelos Sabiá.</p>
            <div className="organic-tag"><span className="direction-dot" /> Um novo espaço para suas ideias</div>
          </div>
          <footer className="direction-footer"><span>Tecnologia que fala a sua língua.</span><span>maritaca.ai</span></footer>
        </>
      )}
    </section>
  );
}
